import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { useId } from 'react'
import { TaxRefundMethodEditor } from '../TaxRefundMethodEditor'
import { ButtonGroup } from '@/components/ButtonGroup'
import type { TaxRefundMethodEditorViewModel } from '../../TaxRefundMethodEditorViewModel'

export interface TaxRefundMethodEditorDialogProps {
  vm: TaxRefundMethodEditorViewModel
  isDisabled?: boolean
  testId?: string
  submitButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * A dialog for viewing and editing tax refund method information.
 */
export const TaxRefundMethodEditorDialog = observer(
  function TaxRefundMethodEditorDialog({
    vm,
    isDisabled,
    testId,
    submitButtonTestId,
    cancelButtonTestId,
  }: TaxRefundMethodEditorDialogProps) {
    const formId = useId()
    const isFormDisabled = vm.save.pending || vm.isReadOnly || isDisabled
    return (
      <ModalDialog
        isOpen={vm.modal.showing}
        onClose={vm.close}
        isDismissable={!vm.save.pending}
        sheetSizing="stretch"
        isUsingDialogScrolling
        size="lg"
        testId={testId}
      >
        <ModalDialogHeader>
          {vm.isReadOnly ? (
            <>
              <ModalDialogTitle>Your refund method</ModalDialogTitle>
              <ModalDialogDescription>
                View your refund method below.
              </ModalDialogDescription>
            </>
          ) : vm.isNew ? (
            <>
              <ModalDialogTitle>
                Manage how you’ll receive your tax refund
              </ModalDialogTitle>
              <ModalDialogDescription>
                Please complete the following so we can confirm your refund
                method.
              </ModalDialogDescription>
            </>
          ) : (
            <>
              <ModalDialogTitle>Edit your refund method</ModalDialogTitle>
              <ModalDialogDescription>
                View your refund method below, and make changes if needed.
              </ModalDialogDescription>
            </>
          )}
        </ModalDialogHeader>

        <ModalDialogContent>
          <TaxRefundMethodEditor
            formId={formId}
            isDisabled={isFormDisabled}
            vm={vm}
          />
        </ModalDialogContent>
        <ModalDialogFooter sticky bordered compact>
          <ButtonGroup stretch>
            <Button
              isDisabled={vm.save.pending}
              variant="outline"
              label={vm.isReadOnly ? 'Close' : 'Cancel'}
              onPress={vm.close}
              testId={cancelButtonTestId}
            />

            {!vm.isReadOnly && (
              <Button
                isPending={vm.save.pending}
                isDisabled={isDisabled}
                variant="default"
                label={vm.isNew ? 'Save' : 'Save changes'}
                type="submit"
                form={formId}
                testId={submitButtonTestId}
              />
            )}
          </ButtonGroup>
        </ModalDialogFooter>
      </ModalDialog>
    )
  },
)
